import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HITTING_RANKINGS = [
  { key: "runs", label: "Runs", description: "Total Runs Scored", higher_is_better: true },
  { key: "avg", label: "AVG", description: "Team Batting Average", higher_is_better: true },
  { key: "obp", label: "OBP", description: "On-Base Percentage", higher_is_better: true },
  { key: "slg", label: "SLG", description: "Slugging Percentage", higher_is_better: true },
  { key: "ops", label: "OPS", description: "On-Base + Slugging", higher_is_better: true },
  { key: "home_runs", label: "HR", description: "Home Runs", higher_is_better: true },
  { key: "walks", label: "BB", description: "Walks (Higher = Better)", higher_is_better: true },
  { key: "strikeouts_h", label: "K", description: "Strikeouts (Lower = Better)", higher_is_better: false },
  { key: "stolen_bases", label: "SB", description: "Stolen Bases", higher_is_better: true },
];

const PITCHING_RANKINGS = [
  { key: "era", label: "ERA", description: "Earned Run Average (Lower = Better)", higher_is_better: false },
  { key: "whip", label: "WHIP", description: "Walks + Hits per IP (Lower = Better)", higher_is_better: false },
  { key: "runs_allowed", label: "RA", description: "Runs Allowed (Lower = Better)", higher_is_better: false },
  { key: "p_strikeouts", label: "K", description: "Strikeouts (Higher = Better)", higher_is_better: true },
  { key: "p_walks", label: "BB", description: "Walks Allowed (Lower = Better)", higher_is_better: false },
  { key: "k_per_9", label: "K/9", description: "Strikeouts per 9 Inn (Higher = Better)", higher_is_better: true },
  { key: "bb_per_9", label: "BB/9", description: "Walks per 9 Inn (Lower = Better)", higher_is_better: false },
  { key: "avg_against", label: "BAA", description: "Batting Average Against (Lower = Better)", higher_is_better: false },
  { key: "saves", label: "SV", description: "Saves", higher_is_better: true },
  { key: "holds", label: "HLD", description: "Holds", higher_is_better: true },
];

function DrilldownModal({ stat, group, onClose }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await axios.get(`${API}/stats/rankings/all-teams/${group}/${stat.key}`);
        setTeams(res.data.teams || []);
      } catch (e) {
        console.error("Error fetching all-teams ranking:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, [group, stat.key]);

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (["avg", "obp", "slg", "ops", "avg_against"].includes(stat.key)) return value.toFixed(3);
    if (["era", "whip", "k_per_9", "bb_per_9"].includes(stat.key)) return value.toFixed(2);
    return Math.round(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="rankings-drilldown-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-astros-surface border border-astros-border w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-astros-border">
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wide">{stat.label} - ALL MLB TEAMS</h3>
            <p className="text-xs text-astros-muted">{stat.description}</p>
          </div>
          <button onClick={onClose} className="text-astros-muted hover:text-white transition-colors" data-testid="drilldown-close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-1">
              {teams.map((team, idx) => (
                <div
                  key={team.team_id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    team.is_astros
                      ? "bg-astros-orange/15 border border-astros-orange/40"
                      : idx % 2 === 0 ? "bg-astros-border/5" : ""
                  }`}
                  data-testid={`drilldown-team-${team.abbreviation}`}
                >
                  <span className={`w-7 text-center font-bebas text-base ${
                    team.rank <= 3 ? "text-astros-orange" : "text-astros-muted"
                  }`}>
                    {team.rank}
                  </span>
                  <span className={`flex-1 font-ibm-plex text-sm ${team.is_astros ? "text-astros-orange font-bold" : "text-white"}`}>
                    {team.name}
                  </span>
                  <span className="text-xs text-astros-muted w-8 text-center">{team.abbreviation}</span>
                  <span className={`font-mono text-sm font-semibold min-w-[60px] text-right ${
                    team.is_astros ? "text-astros-orange" : "text-white"
                  }`}>
                    {formatValue(team.value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function RankingsTab() {
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("hitting");
  const [drilldownStat, setDrilldownStat] = useState(null);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const response = await axios.get(`${API}/stats/rankings`);
        setRankings(response.data);
      } catch (e) {
        console.error("Error fetching rankings:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchRankings();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-astros-orange border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const stats = selectedCategory === "hitting" ? HITTING_RANKINGS : PITCHING_RANKINGS;
  const rankingData = selectedCategory === "hitting" ? rankings?.hitting : rankings?.pitching;

  const getRankBadgeClass = (rank) => {
    if (!rank) return "rank-other";
    if (rank <= 3) return "rank-top-3";
    if (rank <= 10) return "rank-top-10";
    return "rank-other";
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "number") {
      if (["avg", "obp", "slg", "ops", "avg_against"].includes(key)) return value.toFixed(3);
      if (["era", "whip", "k_per_9", "bb_per_9"].includes(key)) return value.toFixed(2);
      return Math.round(value);
    }
    return value;
  };

  return (
    <div data-testid="rankings-tab">
      <h2 className="font-bebas text-2xl sm:text-3xl tracking-tight text-white mb-4">
        MLB & AMERICAN LEAGUE RANKINGS
      </h2>
      <p className="text-astros-muted text-sm mb-6">
        Astros' rank compared to other teams in MLB and the American League. <span className="text-astros-orange">Click any stat</span> to see all 30 teams ranked.
      </p>

      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setSelectedCategory("hitting")}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            selectedCategory === "hitting"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="rankings-hitting"
        >
          HITTING
        </button>
        <button
          onClick={() => setSelectedCategory("pitching")}
          className={`px-4 py-2 font-bebas text-lg tracking-wide transition-colors ${
            selectedCategory === "pitching"
              ? "text-astros-orange border-b-2 border-astros-orange"
              : "text-astros-muted hover:text-white"
          }`}
          data-testid="rankings-pitching"
        >
          PITCHING
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((stat, index) => {
          const data = rankingData?.[stat.key];
          return (
            <motion.div
              key={stat.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-astros-surface border border-astros-border p-4 hover:border-astros-orange transition-colors cursor-pointer group"
              onClick={() => setDrilldownStat(stat)}
              data-testid={`ranking-card-${stat.key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-bebas text-lg text-white">{stat.label}</h4>
                    <ChevronRight size={14} className="text-astros-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-xs text-astros-muted">{stat.description}</p>
                </div>
                <p className="font-bebas text-2xl text-astros-orange">
                  {formatValue(data?.value, stat.key)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-astros-muted uppercase tracking-wider mb-1">MLB Rank</p>
                  <div className="flex items-center gap-2">
                    <span className={`rank-badge ${getRankBadgeClass(data?.mlb_rank)}`}>
                      #{data?.mlb_rank || "-"}
                    </span>
                    <span className="text-xs text-astros-muted">of {data?.total_mlb || 30}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-astros-muted uppercase tracking-wider mb-1">AL Rank</p>
                  <div className="flex items-center gap-2">
                    <span className={`rank-badge ${getRankBadgeClass(data?.al_rank)}`}>
                      #{data?.al_rank || "-"}
                    </span>
                    <span className="text-xs text-astros-muted">of {data?.total_al || 15}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {!rankingData && (
        <div className="text-center py-12 text-astros-muted">
          No ranking data available
        </div>
      )}

      <AnimatePresence>
        {drilldownStat && (
          <DrilldownModal
            stat={drilldownStat}
            group={selectedCategory}
            onClose={() => setDrilldownStat(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
